// FROM: https://github.com/modusign/modusign-client/blob/2772938d47982e2e868b5adbce601cdf9bb0b288/app/src/containers/RequesterAttachmentsCard/RequesterAttachmentList/index.tsx

import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ErrorModal, Flex, Text } from '@modusign/design-system';
import { useAttachments } from '@modusign/react-signing';
import { Attachment } from '@modusign/react-signing/types';
import { useMutation } from 'react-query';

import formatFileSize from '@app/libs/formatter/formatFileSize';
import { openFileFromURL } from '@app/store/transport-layer/files';
import { getFileDownloadUrl } from '@app/store/transport-layer/upload/files/download-url';

interface RequesterAttachmentListProps {
  displayDataLabel?: boolean;
  onBlockPopUp: () => void;
}

function RequesterAttachmentList({ displayDataLabel, onBlockPopUp }: RequesterAttachmentListProps) {
  const { attachments, removeAttachment } = useAttachments();

  const requesterAttachments = attachments.filter(
    (attachment) => attachment.participantReference.type === 'REQUESTER'
  );

  const onRemove = (id: string) => {
    removeAttachment(id);
  };

  const {
    mutate: openAttachment,
    error: openAttachmentError,
    reset: resetOpenAttachment,
  } = useMutation<void, Error, Attachment>({
    mutationFn: async (requesterAttachment: Attachment) => {
      const { signedUrl } = await getFileDownloadUrl({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        fileId: requesterAttachment.input!.fileId,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        token: requesterAttachment.input!.token,
        otp: null,
        fromDocumentBucket: false,
      });

      openFileFromURL(signedUrl, onBlockPopUp);
    },
  });

  if (requesterAttachments.length === 0) {
    return null;
  }

  return (
    <Flex as="ul" flexDirection="column" gap={4}>
      {requesterAttachments.map((requesterAttachment) => (
        <Flex
          as="li"
          bg="buttonBackgroundNeutralBase"
          border="1px solid"
          borderColor="buttonBorderNeutralBase"
          borderRadius="3px"
          justifyContent="space-between"
          key={requesterAttachment.id}
          px={4}
          py={3}
          width="100%"
        >
          <Flex flexDirection="column" gap="6px" width="100%">
            <Text
              color="textNeutralBase"
              ellipsis
              fontSize="xs"
              fontWeight="medium"
              mr={3}
              sx={{
                flex: 'auto',
                overflow: 'hidden',
                '&:hover': {
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: '0.2em',
                },
              }}
              onClick={() => openAttachment(requesterAttachment)}
            >
              {requesterAttachment.file?.name || ''}
              {requesterAttachment.file?.extension} (
              {formatFileSize(requesterAttachment.file?.size || 0)})
            </Text>
            {displayDataLabel && requesterAttachment.dataLabel && (
              <Text as="p" fontSize="xs">
                데이터 라벨: {requesterAttachment.dataLabel}
              </Text>
            )}
          </Flex>
          <Box
            as="button"
            color="textNeutralLight"
            onClick={() => onRemove(requesterAttachment.id)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </Box>
        </Flex>
      ))}
      <ErrorModal
        error={openAttachmentError}
        isOpen
        title="첨부파일 확인 실패"
        onClose={() => resetOpenAttachment()}
      />
    </Flex>
  );
}

export default RequesterAttachmentList;
